import PlatformPage from '../PlatformPage';

const SNIPPET = `// app/layout.js (App Router) — or pages/_app.js for the Pages Router
import Script from 'next/script';

export default function RootLayout({ children }) {
  return (
    <html lang="en">
      <body>
        {children}
        <Script
          src="https://api.cayda.io/widget.js"
          data-token="YOUR_SITE_TOKEN"
          data-api="https://api.cayda.io"
          data-dashboard="https://www.cayda.io"
          strategy="afterInteractive"
        />
      </body>
    </html>
  );
}`;

const ForNextjs = () => (
  <PlatformPage
    slug="nextjs"
    name="Next.js"
    title="Website Feedback Tool for Next.js and React"
    description="Visual feedback on your live Next.js or React app. One script tag, client-side routing handled, works on pages behind login. Invisible to users."
    h1="Website feedback tool built for Next.js and React apps."
    intro="Drop one script tag into your root layout and your team can click on any element of the running app to leave a note pinned to that exact element. Client-side navigation, dynamic routes, authenticated pages and dashboards all work, because your team is using the real app with their own session."
    steps={[
      { title: 'Copy your snippet from the Cayda dashboard', desc: 'One script tag with your site’s token.' },
      { title: 'Add it to your root layout with next/script', desc: 'App Router: app/layout.js. Pages Router: pages/_app.js. Plain React: index.html or your top-level component.' },
      { title: 'Deploy', desc: 'The feedback layer is live for your team on every route.' },
    ]}
    code={SNIPPET}
    stepsNote="Reviewing preview deployments too? Add each hostname as its own site in Cayda so notes from preview and production stay separate."
    loads={{
      title: 'Built for how Next.js apps navigate',
      paragraphs: [
        'Next.js routes on the client through the History API. Cayda intercepts those navigation calls directly, so when a user moves from /pricing to /docs without a page load, it cleans up the previous route’s notes and loads the right ones for the new one. Hash routing and anything unusual is caught by a lightweight fallback that checks the URL every 800 milliseconds.',
        'The script is a plain client-side tag, so it is indifferent to server components, streaming, or how you render. It pins notes to DOM elements, not coordinates, so they survive re-renders and responsive layouts.',
      ],
    }}
    where={{
      title: 'Where your team leaves notes',
      paragraphs: [
        'Marketing pages, the logged-in dashboard, onboarding flows, settings screens, a table that misaligns at 1280px, a modal that traps focus, a copy change product wants before launch. Because the review happens in the real app under the reviewer’s own account, pages behind authentication are just pages. Every note carries a screenshot, the URL, browser, OS and viewport.',
        'Notes have a type, status, priority and assignee, thread replies in place, and land in a Kanban or list dashboard. On Pro they notify Slack; on Team a click turns one into a Jira, Asana or Linear issue with the context attached.',
      ],
    }}
    extras={[
      {
        title: 'Invisible to your users',
        paragraphs: [
          'The script activates only for authenticated Cayda team members and invited guests. For every other user it renders nothing, sets no cookies and adds no meaningful weight, so it can ship in production rather than a separate staging build.',
        ],
      },
    ]}
    ctaTitle="Add feedback to your Next.js app."
    ctaSub="One script tag in your layout, and your team is leaving notes on the running app in minutes."
  />
);

export default ForNextjs;
